<docs>
An on/off switch for a binary setting that takes effect immediately. Built on Reka `Switch`, so
role, `aria-checked`, Space and Enter come for free; clicking the label toggles it too (a `<button>`
is a labelable element, so `<label for>` forwards the activation click). For a value that is only
applied when a form is submitted, and for multi-select lists, use ZoneCheckbox;
ZoneSegmentedControl when there are more than two choices.

The base form is just the switch: `<ZoneToggle v-model="on" />` renders a lone button and nothing
else (no label row, state text, helper line or hidden input), and the wrapper goes `inline-flex` so
it sits in a table cell or a toolbar like any inline control. Pass `ariaLabel` in that case: with no
label and no state text there is nothing to name the switch. Figma's guidance is to prefer a label or
state text and use the bare form sparingly. `tooltip` puts the info icon on the label row when
there is a `label`, and beside the state text otherwise, so a `stateText` toggle (the label on the
right, the production form) keeps its hint on the same line.

## Notes

- Rails: `name` renders a hidden input submitting the string `'on'` or `'off'`, the same encoding as
  the Ruby Zone::Toggle. It fires a native bubbling `change` on every value move so vee-validate's
  ValidationProvider sees the field. Reka's own form-control input is bypassed (it submits checkbox
  semantics, so an off toggle would submit nothing at all).
- The on track is primary-500 (#2aa7ff), the colour of ZoneButton's `primary`: product asked for
  toggles and buttons in primary/500 (the Figma component still draws primary-700).
- The focus ring is the literal Figma value #0f62fe rather than a Zone token, so it stays visible
  against the blue of a switched-on track.
- `size` defaults to `base`, following Figma's written guidance ("the default size for general
  interface use"). The Ruby Zone::Toggle defaults to `:sm`: pass `size="small"` when porting a Rails
  view, and for dense rows or drawers generally.
- `warning` and `invalid` colour the helper line only; the track stays blue/grey in every variant.

Figma: [Toggle](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=1529-34102&m=dev)
</docs>

<template>
  <div :class="wrapperClasses">
    <!--
      #label REPLACES the standard ZoneLabel rather than rendering inside it, so callers can
      compose their own row without nesting a <label> inside a <label>. The wrapping div carries
      `labelId` so the switch's aria-labelledby still resolves whichever way the label is built.
    -->
    <div
      v-if="hasLabel"
      :id="labelId"
    >
      <slot
        v-if="$slots.label"
        name="label"
        :html-for="resolvedId"
        :label-id="labelId"
        :required="required"
        :invalid="invalid"
        :tooltip="tooltip"
      />
      <ZoneLabel
        v-else
        :label="label"
        :required="required"
        :tooltip="tooltip"
        :icon="Boolean(tooltip)"
        :invalid="invalid"
        :html-for="resolvedId"
      />
    </div>

    <div :class="rowClasses">
      <SwitchRoot
        :id="resolvedId"
        :model-value="modelValue"
        :disabled="disabled"
        :required="required"
        :class="trackClasses"
        :aria-label="ariaLabel || undefined"
        :aria-labelledby="labelledBy"
        :aria-describedby="hasHelper ? helperId : undefined"
        :aria-invalid="invalid || undefined"
        :aria-readonly="readonly || undefined"
        @update:model-value="onUpdate"
      >
        <SwitchThumb :class="thumbClasses" />
      </SwitchRoot>

      <span
        v-if="hasStateText"
        :id="stateTextId"
        class="min-w-0 text-sm leading-5 text-gray-900"
        data-testid="state-text"
      >
        <slot name="state-text">{{ stateText }}</slot>
      </span>
      <ZoneIcon
        v-if="tooltip && !hasLabel"
        icon="info"
        :tooltip="tooltip"
        color="gray"
        :shade="600"
        class="shrink-0"
        data-testid="state-tooltip"
      />
    </div>

    <!-- Rails form submission. Reka's built-in form control is a checkbox, which submits nothing
         at all when off, so the on/off encoding of the Ruby Zone::Toggle is rendered here. -->
    <input
      v-if="name"
      ref="hiddenInput"
      type="hidden"
      :name="name"
      :value="modelValue ? 'on' : 'off'"
      :disabled="disabled"
    />

    <HelperText
      v-if="hasHelper"
      :id="helperId"
      :invalid="invalid"
      :warning="warning"
    >
      <slot name="helper">{{ helperText }}</slot>
    </HelperText>
  </div>
</template>

<script lang="ts">
import { computed, nextTick, ref, useId, watch, type PropType } from 'vue';
import { SwitchRoot, SwitchThumb } from 'reka-ui';
import HelperText from './field/HelperText.vue';
import ZoneIcon from './Icon.vue';
import ZoneLabel from './Label.vue';

const SIZES = ['small', 'base'] as const;

export type ToggleSize = (typeof SIZES)[number];

// Track geometry straight from the Figma size table: Base 48x24 with an 18px thumb, Small 32x16
// with a 10px thumb, both inset 3px. The thumb slides on translate-x rather than sitting in a
// padded flex row so the movement can animate; the ON offset is (track - inset - thumb).
const SIZE_CLASSES: Record<ToggleSize, { track: string; thumb: string }> = {
  small: { track: 'h-4 w-8', thumb: 'size-[10px] translate-x-[3px] data-[state=checked]:translate-x-[19px]' },
  base: { track: 'h-6 w-12', thumb: 'size-[18px] translate-x-[3px] data-[state=checked]:translate-x-[27px]' },
};

</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneToggle' });

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  /** 32x16 (`small`) or 48x24 (`base`). */
  size: {
    type: String as PropType<ToggleSize>,
    default: 'base',
    validator: (v: string) => SIZES.includes(v as ToggleSize),
  },
  /** Plain-text label above the switch (or the `#label` slot). */
  label: {
    type: String,
    default: null,
  },
  /** Text beside the switch, describing what is being changed (or the `#state-text` slot). */
  stateText: {
    type: String,
    default: null,
  },
  /** Small text below the switch (or the `#helper` slot). */
  helperText: {
    type: String,
    default: null,
  },
  /** Info-tooltip text: on the label row with a `label`, beside the state text without one. */
  tooltip: {
    type: String,
    default: null,
  },
  /** Accessible name for the bare switch, when there is no label and no state text. */
  ariaLabel: {
    type: String,
    default: null,
  },
  /** Renders a hidden input submitting `'on'` / `'off'`. */
  name: {
    type: String,
    default: null,
  },
  /** Falls back to a generated id. */
  id: {
    type: String,
    default: null,
  },
  required: {
    type: Boolean,
    default: false,
  },
  /** Non-interactive, whole field at 30% opacity, hidden input disabled. */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Shows a value the user cannot change: grey track, grey thumb, still focusable and still
   * submitted. Not the same as `disabled`.
   */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** Red label, red helper text, error icon and `aria-invalid`. */
  invalid: {
    type: Boolean,
    default: false,
  },
  /** Yellow helper text and warning icon (suppressed by `invalid`). */
  warning: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  /** On toggle. */
  'update:modelValue': [value: boolean];
  /** Fires with `update:modelValue`, for form wiring. */
  change: [value: boolean];
}>();

const slots = defineSlots<{
  /**
   * Replaces the whole label row; it does not render inside the built-in ZoneLabel, so render your own
   * ZoneLabel in it to keep the standard look. The `label`/`tooltip` props are not rendered meanwhile.
   */
  label?(props: { htmlFor: string; labelId: string; required: boolean; invalid: boolean; tooltip: string | null }): any;
  /** Text beside the switch; overrides `stateText`. */
  'state-text'?(): any;
  /** The helper line; overrides `helperText`. */
  helper?(): any;
}>();

const hiddenInput = ref<HTMLInputElement | null>(null);

const generatedId = `zone-toggle-${useId()}`;

const resolvedId = computed<string>(() => props.id || generatedId);

const labelId = computed<string>(() => `${resolvedId.value}-label`);

const stateTextId = computed<string>(() => `${resolvedId.value}-state-text`);

const helperId = computed<string>(() => `${resolvedId.value}-helper`);

const hasLabel = computed<boolean>(() => Boolean(props.label || slots.label));

const hasStateText = computed<boolean>(() => Boolean(props.stateText || slots['state-text']));

const hasHelper = computed<boolean>(() => Boolean(props.helperText || slots.helper));

// The bare toggle should behave like an inline control (a table cell, a toolbar) rather than
// a block that claims the full row width. Once there is a label or a helper line the field
// becomes a stacked block again, so long helper text wraps against the container.
const wrapperClasses = computed<string>(() => {
  const stacked = hasLabel.value || hasHelper.value;
  const classes = [stacked ? 'flex flex-col gap-2' : 'inline-flex'];

  if (props.disabled) {
    classes.push('opacity-30');
  }

  return classes.join(' ');
});

// Small pads the row to 20px so a switch-plus-state-text line keeps the same height as the
// base size, which is what the Figma "Small" input wrapper does.
const rowClasses = computed<string>(() => (
  ['flex items-center gap-2', props.size === 'small' ? 'py-0.5' : ''].filter(Boolean).join(' ')
));

const trackClasses = computed<string>(() => {
  const classes = [
    'relative inline-flex shrink-0 select-none items-center rounded-full',
    'border-0 p-0 transition-colors duration-150',
    // Figma draws a flush 2px ring in #0f62fe. There is no Zone token for it, and a token blue
    // would sit too close to the ON track colour to read when switched on.
    'outline-none focus-visible:ring-2 focus-visible:ring-[#0f62fe]',
    SIZE_CLASSES[props.size].track,
  ];

  if (props.readonly) {
    // Inset box-shadow rather than a border so the 1px outline costs no layout space and the
    // thumb offsets stay identical across every variant.
    classes.push('bg-gray-100 shadow-[inset_0_0_0_1px_#d1d5db]');
  } else {
    classes.push('bg-gray-300 data-[state=checked]:bg-primary-500');
  }

  if (props.disabled) {
    classes.push('cursor-not-allowed');
  } else if (props.readonly) {
    classes.push('cursor-default');
  } else {
    classes.push('cursor-pointer');
  }

  return classes.join(' ');
});

const thumbClasses = computed<string>(() => [
  'pointer-events-none block rounded-full transition-transform duration-150',
  props.readonly ? 'bg-gray-500' : 'bg-white',
  SIZE_CLASSES[props.size].thumb,
].join(' '));


// An explicit ariaLabel wins outright; otherwise the switch is named by whichever of the
// label row and the state text are on screen.
const labelledBy = computed<string | undefined>(() => {
  if (props.ariaLabel) {
    return undefined;
  }

  const ids = [hasLabel.value ? labelId.value : null, hasStateText.value ? stateTextId.value : null];

  return ids.filter(Boolean).join(' ') || undefined;
});

// ValidationProvider detects field changes by listening for native DOM events bubbling up
// through its wrapper div. The switch is a button, not a native control, so Vue component
// events never reach that listener — dispatching a native change on the hidden input lets
// ValidationProvider read the current value off event.target.
watch(() => props.modelValue, () => {
  nextTick(() => {
    hiddenInput.value?.dispatchEvent(new Event('change', { bubbles: true }));
  });
});

// Reka has no readonly concept, only disabled — and disabling would drop the switch out of
// the tab order, which a read-only field should stay in. The component is fully controlled
// (modelValue is always passed), so swallowing the update here is enough to freeze it.
function onUpdate(value: boolean) {
  if (props.readonly) {
    return;
  }

  emit('update:modelValue', value);
  emit('change', value);
}
</script>
